import React, {
  ComponentProps,
  Suspense,
  memo,
  useEffect,
  useState,
} from 'react'
import { useNavigate } from 'react-router'

import styled from '@emotion/styled'

import { useEventCapture } from '@surglogs-app/analytics'
import { useHandler } from '@surglogs-app/hooks'
import { lazyWithRetry } from '@surglogs-app/lazy'
import { getDashboardRoute } from '@surglogs-app/routing'
import { Config } from '@surglogs-app/types'
import { DialogLoader } from '@surglogs-app/ui.loader'
import { ModalBackground, ModalContent } from '@surglogs-app/ui.modal'

import { PromoModalContext } from '../constants/Analytics'
import {
  CALENDLY_URL,
  CALENDLY_URL_FILLABLE_DOCUMENTS_PROMO,
} from '../constants/Calendly'
import { PromoAuthData } from '../models'
import { PromoModalFeature } from '../types'

import type { CalendlyWidget as PromoModalCalendlyWidget } from './CalendlyWidget'
import { PromoModalContent } from './PromoModalContent'
import { PromoModalFormValues } from './PromoModalForm'

type CalendlyWidgetProps = ComponentProps<typeof PromoModalCalendlyWidget>

const CalendlyWidget = lazyWithRetry<CalendlyWidgetProps>(() =>
  import('@surglogs-app/promo-modal').then((module) => ({
    default: module.CalendlyWidget,
  })),
)

const StyledModalBackground = styled(ModalBackground)`
  background-color: rgba(3, 50, 91, 0.9);
  backdrop-filter: blur(5px);
  transition: all 0.3s;
  padding-left: 0;
  padding-right: 80px;
`

const StyledModalContent = styled(ModalContent)`
  width: 100%;
  max-width: 1300px;
  background-color: transparent;
  position: relative;
`

const getCalendlyUrl = (featureName: PromoModalFeature) => {
  if (featureName === 'COMPETENCIES' || featureName === 'FILLABLE_DOCUMENTS') {
    return CALENDLY_URL_FILLABLE_DOCUMENTS_PROMO
  }

  return CALENDLY_URL
}

interface PrefillData extends PromoModalFormValues {
  name?: string
}

interface PromoModalProps {
  featureName: PromoModalFeature
  config: Config
  hideModal?: () => void
  onSuccess?: () => void
  context: PromoModalContext
  authData?: PromoAuthData
  calendlyUrl?: string
}

export const PromoModal = memo(
  ({
    featureName,
    config,
    hideModal,
    onSuccess,
    context,
    authData,
    calendlyUrl,
    ...rest
  }: PromoModalProps) => {
    const navigate = useNavigate()
    const { captureEvent } = useEventCapture()

    const onCancel = useHandler(() => {
      if (hideModal) {
        hideModal()
      } else {
        navigate(getDashboardRoute())
      }
    })

    const [prefill, setPrefill] = useState<PrefillData | undefined>()

    const onSubmitSuccess = useHandler((data: PromoModalFormValues) => {
      setPrefill({
        email: data.email,
        name: authData?.userName ?? '',
      })
      onSuccess?.()
    })

    useEffect(() => {
      captureEvent('promo_viewed', {
        type: featureName,
        context,
      })
    }, [captureEvent, featureName, context])

    return (
      <StyledModalBackground>
        {prefill ? (
          <Suspense {...{ fallback: <DialogLoader /> }}>
            <CalendlyWidget
              {...{
                open: !!prefill,
                onCancel,
                prefill,
                url: getCalendlyUrl(featureName),
                trialRequestCalendlyUrl: config.trialRequestCalendlyUrl,
              }}
            />
          </Suspense>
        ) : (
          <StyledModalContent {...rest}>
            <PromoModalContent
              {...{
                onCancel,
                featureName,
                onSubmitSuccess,
                config,
                context,
                authData,
                clinicId: authData?.clinicId,
              }}
            />
          </StyledModalContent>
        )}
      </StyledModalBackground>
    )
  },
)
